import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { wheelService } from '../api/wheel';
import { logoutUser } from './authSlice';

export interface ClaimItem {
  imageUrl: any;
  gameImageUrl: any;
  gameId: any;
  gameTitle: string;
  validUntil: any;
  queueOrder: number;
  spinId: number;
  type: string;
  label: string;
  spunAt: string;
  status: string;
  statusLabel: string;
  claimable: boolean;
  lockedReason: string | null;
  expiresAt?: string;
  gameName?: string;
}

export interface Category {
  type: string;
  kicker: string;
  title: string;
  subtitle: string;
  status: string;
  statusLabel: string;
  claimable: boolean;
  cash?: {
    total: string;
    currencyCode: string;
    count: number;
    spunAt: string;
  } | null;
  depositMatchPercent?: string | number | null;
  freeSpinCount?: string | number | null;
  claim: ClaimItem[];
  queue: ClaimItem[];
}

export interface UserWheelState {
  availableSpins: number;
  categories: Category[];
  queue: ClaimItem[];
  queueLimit: number;
  queuePage: number;
  queueTotal: number;
  dailySpin: any;
  isLoading: boolean;
  error: string | null;
}

const initialState: UserWheelState = {
  availableSpins: 0,
  categories: [],
  queue: [],
  queueLimit: 5,
  queuePage: 1,
  queueTotal: 0,
  dailySpin: null,
  isLoading: false,
  error: null,
};

export const fetchUserWheelData = createAsyncThunk(
  'userWheel/fetchData',
  async (params: { page?: number; limit?: number } | void, { rejectWithValue }) => {
    try {
      const response = await wheelService.getUserWheelData(params || undefined);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wheel data');
    }
  },
  {
    // Disable default deduplication — each spin changes server state,
    // so we must always fetch fresh data even if a previous fetch is pending.
    condition: () => true,
  },
);

export const userWheelSlice = createSlice({
  name: 'userWheel',
  initialState,
  reducers: {
    resetUserWheel: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(logoutUser.fulfilled, () => initialState)
      .addCase(logoutUser.rejected, () => initialState)
      .addCase(fetchUserWheelData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserWheelData.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload || {};
        state.availableSpins = payload.availableSpins || 0;
        state.categories = payload.categories || [];
        state.queue = payload.queue || [];
        state.queueLimit = typeof payload.queueLimit === 'number' ? payload.queueLimit : 5;
        state.queuePage = typeof payload.queuePage === 'number' ? payload.queuePage : 1;
        state.queueTotal =
          typeof payload.queueTotal === 'number' ? payload.queueTotal : payload.queue?.length || 0;
        state.dailySpin = payload.dailySpin || null;
      })
      .addCase(fetchUserWheelData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetUserWheel } = userWheelSlice.actions;

export default userWheelSlice.reducer;
